import React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import pkg from '../../package.json';

export function AuthDebug() {
  const { user, session, profileId, schoolId, userProfile, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading) {
      console.log('🔎 AuthDebug context snapshot', {
        email: user?.email || null,
        profileId: profileId || null,
        schoolId: schoolId || null,
        profileSchoolId: userProfile?.school_id || null
      });
    }
  }, [loading, user, profileId, schoolId, userProfile]);
  const [rlsStatus, setRlsStatus] = useState<{ checking: boolean; pass: boolean | null; details: string }>(() => ({ checking: false, pass: null, details: '' }))
  const [minimized, setMinimized] = useState<boolean>(false)

  const testQueries = async () => {
    console.log('🧪 Testing database access...');

    // Test structure_rooms access
    try {
      const { data: rooms, error: roomError } = await supabase
        .from('structure_rooms')
        .select('id, name')
        .limit(5);
      console.log('✅ Rooms query:', { data: rooms, error: roomError });
    } catch (err) {
      console.error('❌ Rooms query failed:', err);
    }

    // Test staff access
    try {
      const { data: staff, error: staffError } = await supabase
        .from('vw_react_staff_management')
        .select('id, first_name, last_name')
        .limit(5);
      console.log('✅ Staff query:', { data: staff, error: staffError });
    } catch (err) {
      console.error('❌ Staff query failed:', err);
    }

    // Test session
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      console.log('🔑 Current session:', session ? 'Valid' : 'None', error);
    } catch (err) {
      console.error('❌ Session check failed:', err);
    }
  };

  const runFamilyRlsCheck = async () => {
    setRlsStatus({ checking: true, pass: null, details: 'Prüfe RLS…' })
    try {
      const parentId = profileId as string | null;
      const currentSchoolId = schoolId as string | null;
      if (!parentId || !currentSchoolId) {
        setRlsStatus({ checking: false, pass: false, details: 'Kein profileId oder schoolId vorhanden.' });
        return;
      }

      // 1) Allowed children for this parent (baseline: my links with access_restricted=false)
      const { data: links, error: linkErr } = await supabase
        .from('family_member_child_links')
        .select('child_profile_id')
        .eq('adult_profile_id', parentId)
        .eq('school_id', currentSchoolId)
        .eq('access_restricted', false);

      if (linkErr) {
        setRlsStatus({ checking: false, pass: false, details: `Fehler beim Lesen der Familien-Links: ${linkErr.message}` })
        return;
      }
      const allowedChildIds = new Set<string>((links || []).map((l: any) => String(l.child_profile_id)));

      // 2) My family ids (to distinguish same-family vs other families)
      const { data: myFamilies, error: famErr } = await supabase
        .from('family_members')
        .select('family_id')
        .eq('profile_id', parentId)
        .eq('school_id', currentSchoolId)
        .is('removed_at', null);
      if (famErr) {
        setRlsStatus({ checking: false, pass: false, details: `Fehler beim Lesen meiner Familien: ${famErr.message}` })
        return;
      }
      const myFamilyIds: string[] = (myFamilies || []).map((f: any) => String(f.family_id));

      // 3) Attempt to read links from OTHER families (not mine) — should be blocked/empty
      let blockedOther = true;
      if (myFamilyIds.length === 0) {
        blockedOther = true; // no families -> treat as blocked
      } else {
        // Use NOT IN for families outside my membership
        const inList = `(${myFamilyIds.join(',')})`;
        const { data: otherLinks, error: otherErr } = await supabase
          .from('family_member_child_links')
          .select('id')
          .not('family_id', 'in', inList)
          .eq('school_id', currentSchoolId)
          .limit(1);
        blockedOther = !!otherErr || !otherLinks || otherLinks.length === 0;
      }

      // 4) Cross-check: children profiles are within allowed set
      const { data: childrenProfiles, error: cpErr } = await supabase
        .from('user_profiles')
        .select('id')
        .in('id', Array.from(allowedChildIds) as any)
        .eq('school_id', currentSchoolId)
        .limit(50);
      if (cpErr) {
        setRlsStatus({ checking: false, pass: false, details: `Fehler beim Lesen der Kinder-Profile: ${cpErr.message}` })
        return;
      }
      const allWithin = (childrenProfiles || []).every((cp: any) => allowedChildIds.has(String(cp.id)));

      // 5) Optional: other adults in my families should be visible via user_profiles
      let adultsOk = true;
      const { data: famAdults, error: faErr } = await supabase
        .from('family_members')
        .select('profile_id, role, family_id')
        .in('family_id', myFamilyIds.length ? myFamilyIds : ['00000000-0000-0000-0000-000000000000'])
        .eq('school_id', currentSchoolId)
        .is('removed_at', null);
      if (!faErr && famAdults && famAdults.length > 0) {
        const otherAdultIds = Array.from(new Set(
          (famAdults as any[])
            .filter(a => String(a.profile_id) !== parentId && String(a.role).toLowerCase() === 'parent')
            .map(a => String(a.profile_id))
        ));
        if (otherAdultIds.length > 0) {
          const { data: adultProfiles, error: apErr } = await supabase
            .from('user_profiles')
            .select('id')
            .in('id', otherAdultIds as any)
            .eq('school_id', currentSchoolId);
          adultsOk = !apErr && adultProfiles !== null && adultProfiles.length === otherAdultIds.length;
        }
      }

      const pass = blockedOther && allWithin && adultsOk;
      const details = `Erlaubte Kinder: ${allowedChildIds.size}; Andere Familien lesbar: ${blockedOther ? 'nein' : 'ja'}; Sichtbare Kinder in Menge: ${allWithin ? 'ja' : 'nein'}; Andere Erwachsene sichtbar: ${adultsOk ? 'ja' : 'nein'}`;
      setRlsStatus({ checking: false, pass, details });
    } catch (e: any) {
      setRlsStatus({ checking: false, pass: false, details: e?.message || 'Unbekannter Fehler' })
    }
  }

  if (loading) return <div>Loading auth...</div>;

  const APP_VERSION = (import.meta as any).env?.VITE_APP_VERSION || (pkg as any)?.version || '0.0.0'
  const GIT_COMMIT = ((import.meta as any).env?.VITE_GIT_COMMIT || '').toString().slice(0, 7)

  const forceUpdate = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const reg of regs) {
          try { await reg.unregister(); } catch { }
        }
      }
      if ((window as any).caches && typeof caches.keys === 'function') {
        const names = await caches.keys();
        await Promise.all(names.map(n => caches.delete(n)));
      }
      try { sessionStorage.clear(); } catch { }
      try { localStorage.removeItem('VITE_APP_VERSION'); } catch { }
    } finally {
      window.location.reload();
    }
  }

  const kioskToken = typeof window !== 'undefined' ? localStorage.getItem('fw_kiosk_jwt') : null;

  const resetKiosk = () => {
    if (confirm('Kiosk Reset: This will delete the local JWT and return to pairing screen. Continue?')) {
      localStorage.removeItem('fw_kiosk_jwt');
      window.location.replace('/kiosk/setup');
    }
  };

  return (
    <div className="fixed bottom-4 right-4 bg-white border rounded shadow-lg text-xs max-w-sm z-[9999]">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b bg-gray-50/50">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-slate-700">Auth Debug</h3>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 border text-[10px] text-gray-500">
            <span>v{APP_VERSION}</span>
            {GIT_COMMIT && <span>({GIT_COMMIT})</span>}
          </span>
        </div>
        <button
          onClick={() => setMinimized(m => !m)}
          className="px-2 py-0.5 rounded border bg-white hover:bg-gray-50 text-[11px]"
          title={minimized ? 'Maximieren' : 'Minimieren'}
        >
          {minimized ? '▲' : '—'}
        </button>
      </div>

      {/* Minimized content */}
      {minimized ? (
        <div className="px-4 py-2">
          {kioskToken ? (
            <div className="text-amber-600 font-medium">Kiosk Mode Active</div>
          ) : (
            <div>User: {user?.email || 'None'}</div>
          )}
        </div>
      ) : (
        <div className="p-4 space-y-3 max-h-[80vh] overflow-y-auto">
          {kioskToken && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded space-y-2">
              <div className="font-bold text-amber-800 flex justify-between items-center">
                <span>🖥️ Kiosk Session</span>
                <button
                  onClick={resetKiosk}
                  className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 border border-red-300 rounded text-[10px]"
                >
                  Reset / Logout
                </button>
              </div>
              <div className="text-[10px] font-mono break-all bg-white p-1 rounded border border-amber-100 max-h-24 overflow-y-auto">
                {kioskToken}
              </div>
            </div>
          )}

          <div className="space-y-1">
            <div className="font-semibold text-gray-900 border-b pb-1 mb-1">Standard Auth</div>
            <div>User: <span className="font-mono">{user?.email || 'None'}</span></div>
            <div>Profile ID: <span className="font-mono text-[10px]">{profileId || 'None'}</span></div>
            <div>School ID: <span className="font-mono text-[10px]">{schoolId || 'None'}</span></div>
            <div>Role: <span className="font-medium">{userProfile?.role || 'None'}</span></div>
          </div>

          <div className="flex gap-2 pt-2 border-t">
            <button
              onClick={testQueries}
              className="flex-1 px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100"
            >
              Test DB Access
            </button>
            <button
              onClick={forceUpdate}
              className="px-2 py-1 bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200"
              title="Clears Service Worker & caches, then reloads"
            >
              Force Update
            </button>
          </div>

          <div className="pt-2 border-t space-y-2">
            <div className="font-semibold text-gray-900">RLS Checker</div>
            <div className={`p-2 rounded border text-[11px] ${rlsStatus.pass === true ? 'bg-green-50 border-green-200 text-green-800' : rlsStatus.pass === false ? 'bg-red-50 border-red-200 text-red-800' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
              <div className="font-medium mb-1">
                {rlsStatus.checking ? 'Prüfung läuft…' : rlsStatus.pass === true ? '✅ PASS' : rlsStatus.pass === false ? '❌ FAIL' : '—'}
              </div>
              <div className="opacity-90 leading-tight">{rlsStatus.details || 'Noch nicht geprüft'}</div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={runFamilyRlsCheck}
                className="flex-1 px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-100"
              >
                Check RLS
              </button>
              {(userProfile?.role === 'Super Admin') && (
                <button
                  onClick={() => navigate('/debug/rls-inspector')}
                  className="px-2 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded hover:bg-purple-100"
                >
                  RLS Inspector
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
